<template>
  <div class="inventory-page">
    <div class="section-normal bg-color-primary" style="padding-top: 120px;">
      <div class="container">

        <div class="row">

          <!-- ── MOBILE DRAWER OVERLAY ── -->
          <div class="drawer-overlay" :class="{ 'drawer-overlay--visible': mobileFiltersOpen }" @click="mobileFiltersOpen = false"></div>

          <!-- ── LEFT SIDEBAR FILTERS ── -->
          <div class="col-lg-3 col-xl-2 mb-5 mb-lg-0">

            <!-- Mobile filter toggle -->
            <button class="filter-mobile-toggle d-lg-none w-100 mb-3" @click="mobileFiltersOpen = !mobileFiltersOpen">
              <span>Filters</span>
              <span v-if="activeFilterCount > 0" class="filter-count-badge">{{ activeFilterCount }}</span>
              <span class="toggle-arrow" :class="{ open: mobileFiltersOpen }">&#8964;</span>
            </button>

            <div class="filter-sidebar" :class="{ 'filter-sidebar--open': mobileFiltersOpen }">

              <!-- Header -->
              <div class="filter-sidebar--header">
                <span class="filter-sidebar--title">Filters</span>
                <div class="d-flex align-items-center gap-3">
                  <button v-if="activeFilterCount > 0" class="btn-clear-all" @click="clearAllFilters">Clear all</button>
                  <button class="drawer-close d-lg-none" @click="mobileFiltersOpen = false" aria-label="Close filters">&#10005;</button>
                </div>
              </div>

              <!-- BRAND -->
              <div class="filter-group" v-if="availableBrands.length > 0">
                <div class="filter-group--label filter-group--toggle" @click="toggleGroup('brands')">
                  <span>Brand</span>
                  <span class="filter-group--badge" v-if="filters.brands.length">{{ filters.brands.length }}</span>
                  <span class="filter-group--arrow" :class="{ open: expandedGroups.brands }">&#8964;</span>
                </div>
                <div v-show="expandedGroups.brands" class="filter-group--body">
                  <label v-for="brand in availableBrands" :key="brand" class="filter-checkbox">
                    <input type="checkbox" :value="brand" v-model="filters.brands" />
                    <span class="filter-checkbox--text">{{ brand }}</span>
                    <span class="filter-checkbox--count">{{ brandCount(brand) }}</span>
                  </label>
                </div>
              </div>

              <!-- MODEL -->
              <div class="filter-group" v-if="availableModels.length > 0">
                <div class="filter-group--label filter-group--toggle" @click="toggleGroup('models')">
                  <span>Model</span>
                  <span class="filter-group--badge" v-if="filters.models.length">{{ filters.models.length }}</span>
                  <span class="filter-group--arrow" :class="{ open: expandedGroups.models }">&#8964;</span>
                </div>
                <div v-show="expandedGroups.models" class="filter-group--body">
                  <label v-for="model in availableModels" :key="model" class="filter-checkbox">
                    <input type="checkbox" :value="model" v-model="filters.models" />
                    <span class="filter-checkbox--text">{{ model }}</span>
                  </label>
                </div>
              </div>

              <!-- YEAR -->
              <div class="filter-group">
                <div class="filter-group--label">Year</div>
                <div class="filter-range">
                  <input type="number" class="filter-input" v-model.number="filters.yearMin"
                    :placeholder="yearBounds.min || 'From'" :min="yearBounds.min" :max="yearBounds.max" />
                  <span class="filter-range--sep">–</span>
                  <input type="number" class="filter-input" v-model.number="filters.yearMax"
                    :placeholder="yearBounds.max || 'To'" :min="yearBounds.min" :max="yearBounds.max" />
                </div>
              </div>

              <!-- PRICE -->
              <div class="filter-group">
                <div class="filter-group--label">Price Netto (€)</div>
                <div class="filter-range">
                  <input type="number" class="filter-input" v-model.number="filters.priceMin" placeholder="Min" />
                  <span class="filter-range--sep">–</span>
                  <input type="number" class="filter-input" v-model.number="filters.priceMax" placeholder="Max" />
                </div>
              </div>

              <!-- MILEAGE -->
              <div class="filter-group" v-if="hasMileageData">
                <div class="filter-group--label">Mileage (km)</div>
                <div class="filter-range">
                  <input type="number" class="filter-input" v-model.number="filters.mileageMin" placeholder="Min" />
                  <span class="filter-range--sep">–</span>
                  <input type="number" class="filter-input" v-model.number="filters.mileageMax" placeholder="Max" />
                </div>
              </div>

            </div>
          </div>

          <!-- ── RIGHT: COUNT + CARDS ── -->
          <div class="col-lg-9 col-xl-10">

            <!-- Results bar -->
            <div class="results-bar mb-4">
              <div v-if="!loading" class="inventory-count text-color-quinary text-uppercase">
                {{ filteredCars.length }} vehicle{{ filteredCars.length !== 1 ? 's' : '' }} sold
              </div>
            </div>

            <!-- Car list -->
            <div class="row">
              <div v-for="car in paginatedCars" :key="car.id" class="col-12">
                <CarItem :car="car" />
              </div>

              <div v-if="loading" class="col-12 text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-color-quinary">Loading vehicles...</p>
              </div>

              <div v-if="error && !loading" class="col-12 text-center py-5">
                <p class="text-danger">{{ error }}</p>
              </div>

              <div v-if="!loading && !error && paginatedCars.length === 0" class="col-12 text-center py-5">
                <p class="text-color-quinary">No sold vehicles found matching your criteria.</p>
                <button class="btn-clear-all mt-3" @click="clearAllFilters">Clear filters</button>
              </div>
            </div>

            <!-- Pagination -->
            <div class="row mt-5" v-if="totalPages > 1">
              <div class="col-12">
                <div class="pagination-container">
                  <div class="pagination-buttons">
                    <button class="pagination-btn prev" :class="{ disabled: currentPage === 1 }"
                      @click.prevent="changePage(currentPage - 1)" :disabled="currentPage === 1">
                      <span class="d-none d-md-inline">Previous</span>
                      <span class="d-md-none">&laquo;</span>
                    </button>
                    <button v-for="page in displayedPages" :key="page" class="pagination-btn page"
                      :class="{ active: currentPage === page }" @click.prevent="changePage(page)">
                      {{ page }}
                    </button>
                    <button class="pagination-btn next" :class="{ disabled: currentPage === totalPages }"
                      @click.prevent="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                      <span class="d-none d-md-inline">Next</span>
                      <span class="d-md-none">&raquo;</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div><!-- /col-lg-9 -->
        </div><!-- /row -->
      </div>
    </div>

    <AppMap />
    <AppMarquee />
  </div>
</template>

<script>
import CarItem from '../components/CarItem.vue';
import AppMap from '../components/AppMap.vue';
import AppMarquee from '../components/AppMarquee.vue';
import axios from 'axios';

export default {
  components: { CarItem, AppMap, AppMarquee },

  data() {
    return {
      cars: [],
      loading: false,
      error: null,
      currentPage: 1,
      itemsPerPage: 20,
      mobileFiltersOpen: false,
      expandedGroups: { brands: false, models: false },
      filters: {
        brands: [],
        models: [],
        yearMin: null,
        yearMax: null,
        priceMin: null,
        priceMax: null,
        mileageMin: null,
        mileageMax: null,
      },
    };
  },

  mounted() {
    window.scrollTo(0, 0);
    import('../seo.js').then(({ setPageSeo }) => setPageSeo({
      title: 'Exported Cars | 1000+ Vehicles Delivered Worldwide | Selected Cars',
      description: 'View 1000+ premium vehicles successfully exported from Europe worldwide. BMW, Mercedes, Porsche, Lamborghini, Ferrari delivered to Middle East, Asia, Africa and across Europe. Proof of our international export expertise and satisfied clients in 16 countries.',
      keywords: 'exported cars from europe, delivered luxury cars worldwide, car export track record, european cars exported to UAE, cars exported to middle east, cars exported to asia, international car delivery history, exported BMW Mercedes Porsche, car dealer export portfolio, successful vehicle exports europe, trusted car exporter references',
      breadcrumbs: [
        { name: 'Home', url: '/' },
        { name: 'Sold Cars', url: '/sold-cars' }
      ]
    }));
    axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    this.fetchCars();
  },

  watch: {
    filters: { deep: true, handler() { this.currentPage = 1; } },
  },

  computed: {
    availableBrands() {
      return [...new Set(this.baseExcluding('brands').map(c => c.brand))].filter(Boolean).sort();
    },
    availableModels() {
      return [...new Set(this.baseExcluding('models').map(c => c.model))].filter(Boolean).sort();
    },
    hasMileageData() {
      return this.baseExcluding('mileage').some(c => c.mileage);
    },
    yearBounds() {
      const years = this.cars.map(c => c.year).filter(Boolean);
      return { min: Math.min(...years) || null, max: Math.max(...years) || null };
    },
    activeFilterCount() {
      let n = 0;
      if (this.filters.brands.length) n++;
      if (this.filters.models.length) n++;
      if (this.filters.yearMin || this.filters.yearMax) n++;
      if (this.filters.priceMin || this.filters.priceMax) n++;
      if (this.filters.mileageMin || this.filters.mileageMax) n++;
      return n;
    },

    filteredCars() {
      let result = this.cars.filter(car => car.status === 'Sold');

      if (this.filters.brands.length)
        result = result.filter(c => this.filters.brands.includes(c.brand));
      if (this.filters.models.length)
        result = result.filter(c => this.filters.models.includes(c.model));
      if (this.filters.yearMin)
        result = result.filter(c => c.year >= this.filters.yearMin);
      if (this.filters.yearMax)
        result = result.filter(c => c.year <= this.filters.yearMax);
      if (this.filters.priceMin)
        result = result.filter(c => (c.nettoPrice || 0) >= this.filters.priceMin);
      if (this.filters.priceMax)
        result = result.filter(c => (c.nettoPrice || 0) <= this.filters.priceMax);
      if (this.filters.mileageMin)
        result = result.filter(c => c.mileage && c.mileage >= this.filters.mileageMin);
      if (this.filters.mileageMax)
        result = result.filter(c => c.mileage && c.mileage <= this.filters.mileageMax);

      result.sort((a, b) => b.year - a.year);
      return result;
    },

    totalPages() {
      return Math.ceil(this.filteredCars.length / this.itemsPerPage);
    },
    paginatedCars() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredCars.slice(start, start + this.itemsPerPage);
    },
    displayedPages() {
      const pages = [];
      const max = 5;
      if (this.totalPages <= max) {
        for (let i = 1; i <= this.totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        let start = Math.max(2, this.currentPage - 1);
        let end   = Math.min(this.totalPages - 1, this.currentPage + 1);
        if (this.currentPage <= 2) end = 4;
        else if (this.currentPage >= this.totalPages - 1) start = this.totalPages - 3;
        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }
      return pages;
    },
  },

  methods: {
    baseExcluding(group) {
      let r = this.cars.filter(c => c.status === 'Sold');
      if (group !== 'brands' && this.filters.brands.length)
        r = r.filter(c => this.filters.brands.includes(c.brand));
      if (group !== 'models' && this.filters.models.length)
        r = r.filter(c => this.filters.models.includes(c.model));
      if (group !== 'year') {
        if (this.filters.yearMin) r = r.filter(c => c.year >= this.filters.yearMin);
        if (this.filters.yearMax) r = r.filter(c => c.year <= this.filters.yearMax);
      }
      if (group !== 'price') {
        if (this.filters.priceMin) r = r.filter(c => (c.nettoPrice || 0) >= this.filters.priceMin);
        if (this.filters.priceMax) r = r.filter(c => (c.nettoPrice || 0) <= this.filters.priceMax);
      }
      if (group !== 'mileage') {
        if (this.filters.mileageMin) r = r.filter(c => c.mileage && c.mileage >= this.filters.mileageMin);
        if (this.filters.mileageMax) r = r.filter(c => c.mileage && c.mileage <= this.filters.mileageMax);
      }
      return r;
    },

    brandCount(brand) {
      return this.baseExcluding('brands').filter(c => c.brand === brand).length;
    },

    toggleGroup(key) {
      this.expandedGroups[key] = !this.expandedGroups[key];
    },

    clearAllFilters() {
      this.filters = {
        brands: [], models: [],
        yearMin: null, yearMax: null,
        priceMin: null, priceMax: null,
        mileageMin: null, mileageMax: null,
      };
    },

    changePage(page) {
      if (typeof page !== 'number' || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      window.scrollTo({ top: document.querySelector('.section-normal').offsetTop - 100, behavior: 'smooth' });
    },

    async fetchCars() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get('/api/vehicles');
        const vehiclesData = Array.isArray(response.data) ? response.data
          : (response.data.data ? response.data.data : [response.data]);

        this.cars = vehiclesData.map(vehicle => {
          const parsed = this.parseVehicleFields(vehicle);
          return {
            id: vehicle.id,
            brand: vehicle.make,
            model: parsed.model,
            year: vehicle.year,
            trimLine: parsed.trimLine,
            nettoPrice: vehicle.netto_price || vehicle.price,
            grossPrice: vehicle.gross_price || Math.round((vehicle.netto_price || vehicle.price) * 1.2),
            status: this.mapVehicleStatus(vehicle.status),
            image: vehicle.primary_image ? vehicle.primary_image.url : null,
            mileage: vehicle.mileage || null,
            transmission: vehicle.transmission || null,
            enginePower: vehicle.engine_power || null,
            drive: vehicle.drive || null,
          };
        });
      } catch (err) {
        console.error('Error fetching sold vehicles:', err);
        this.error = 'Failed to load vehicles. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    /**
     * Parse model + trim_line fields from raw vehicle data.
     * Handles mobile.de imports where the full ad title (Brand Model*Feature*Feature...)
     * may be stored in either the model field or trim_line field.
     */
    parseVehicleFields(vehicle) {
      const brand = (vehicle.make || '').trim();
      let model = (vehicle.model || '').trim();
      let trimLine = (vehicle.trim_line || '').trim();

      // If model contains *, the full mobile.de string was put in model
      if (model.includes('*')) {
        const parts = model.split('*').map(p => p.trim()).filter(Boolean);
        let firstPart = parts[0];
        // Remove brand name prefix if present (e.g. "Mercedes-Benz AMG G 63" → "AMG G 63")
        if (brand && firstPart.toLowerCase().startsWith(brand.toLowerCase())) {
          firstPart = firstPart.substring(brand.length).trim();
        }
        model = firstPart || parts[0];
        trimLine = parts.slice(1).join(', ');
      }

      // If trimLine contains *, clean it up
      if (trimLine.includes('*')) {
        const parts = trimLine.split('*').map(p => p.trim()).filter(Boolean);
        let startIdx = 0;
        // Skip first segment if it's just brand+model repeated
        if (parts[0] && brand) {
          const first = parts[0].toLowerCase();
          const brandModel = (brand + ' ' + model).toLowerCase();
          if (first.startsWith(brand.toLowerCase()) || first === brandModel) {
            startIdx = 1;
          }
        }
        trimLine = parts.slice(startIdx).join(', ');
      }

      return { model, trimLine };
    },

    mapVehicleStatus(status) {
      if (!status) return 'Unavailable';
      switch (status.toLowerCase()) {
        case 'active':      return 'Available';
        case 'sold':        return 'Sold';
        case 'maintenance': return 'Coming Soon';
        case 'inactive':    return 'Unavailable';
        default:            return status;
      }
    },
  },
};
</script>

<style scoped>
/* ── INVENTORY COUNT ── */
.inventory-count {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
}

/* ── SIDEBAR ── */
.filter-sidebar {
  position: sticky;
  top: 100px;
}

.filter-sidebar--header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  margin-bottom: 0.5rem;
}

.filter-sidebar--title {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #ffffff;
}

.btn-clear-all {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  letter-spacing: 0.05em;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 0.2s;
}
.btn-clear-all:hover { color: #fff; }

.filter-group {
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.filter-group--label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 0.75rem;
}

.filter-group--toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
  transition: color 0.2s;
}
.filter-group--toggle:hover { color: rgba(255, 255, 255, 0.6); }

.filter-group--arrow {
  margin-left: auto;
  font-size: 1rem;
  line-height: 1;
  transition: transform 0.25s ease;
  display: inline-block;
}
.filter-group--arrow.open { transform: rotate(180deg); }

.filter-group--badge {
  background-color: #fff;
  color: #000;
  font-size: 0.55rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 20px;
  margin-left: 0.5rem;
}

.filter-group--body { margin-top: 0.75rem; }

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.3rem 0;
  cursor: pointer;
  width: 100%;
}

.filter-checkbox input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 14px;
  height: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;
}
.filter-checkbox input[type="checkbox"]:checked {
  background-color: #fff;
  border-color: #fff;
}
.filter-checkbox input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 3px; top: 1px;
  width: 5px; height: 8px;
  border: 2px solid #000;
  border-top: none; border-left: none;
  transform: rotate(45deg);
}

.filter-checkbox--text {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  flex: 1;
  transition: color 0.2s;
}
.filter-checkbox:hover .filter-checkbox--text { color: #fff; }
.filter-checkbox input:checked ~ .filter-checkbox--text { color: #fff; }

.filter-checkbox--count {
  font-size: 0.62rem;
  color: rgba(255, 255, 255, 0.2);
}

.filter-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.filter-range--sep {
  color: rgba(255, 255, 255, 0.2);
  font-size: 0.8rem;
}

.filter-input {
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 0.75rem;
  padding: 0.45rem 0.6rem;
  outline: none;
  transition: border-color 0.2s;
  -moz-appearance: textfield;
}
.filter-input::-webkit-outer-spin-button,
.filter-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.filter-input:focus { border-color: rgba(255, 255, 255, 0.35); }
.filter-input::placeholder { color: rgba(255, 255, 255, 0.2); }

/* Mobile toggle */
.filter-mobile-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  cursor: pointer;
}

.filter-count-badge {
  background: #fff;
  color: #000;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 20px;
}

.toggle-arrow {
  font-size: 1rem;
  transition: transform 0.3s;
}
.toggle-arrow.open { transform: rotate(180deg); }

/* ── MOBILE DRAWER ── */
.drawer-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 1049;
  backdrop-filter: blur(2px);
}
.drawer-overlay--visible { display: block; }

.drawer-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
}
.drawer-close:hover { color: #fff; }

@media (max-width: 991px) {
  .filter-sidebar {
    position: fixed;
    top: 0; left: 0;
    height: 100dvh;
    width: min(85vw, 360px);
    background: #0e0e0e;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    z-index: 1050;
    overflow-y: auto;
    padding: 1.5rem 1.25rem;
    transform: translateX(-100%);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .filter-sidebar--open { transform: translateX(0); }
}

/* ── RESULTS BAR ── */
.results-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding-bottom: 1rem;
}

/* ── PAGINATION ── */
.pagination-container {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}
.pagination-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.pagination-btn {
  padding: 10px 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background-color: transparent;
  transition: all 0.3s ease;
  min-width: 45px;
  text-align: center;
}
.pagination-btn:hover { background-color: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.4); }
.pagination-btn.disabled { opacity: 0.35; cursor: not-allowed; }
.pagination-btn.active { background-color: #fff; color: #000; border-color: #fff; }
.pagination-btn.prev, .pagination-btn.next { min-width: 100px; }

@media (max-width: 768px) {
  .pagination-btn { padding: 8px 12px; min-width: 40px; }
  .pagination-btn.prev, .pagination-btn.next { min-width: 40px; }
}
</style>
